import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.css";
import { track } from "../../../analytics";
import { useGoogleMapsScript } from "../../../utils/functions/googleMapsLoader";

/**
 * A React component that provides a Google Maps Places Autocomplete input
 * for searching and selecting address locations. Upon selection, it extracts
 * structured data such as city, state, postal code, coordinates, and the full address.
 *
 * @param {Object} props
 * @param {string} props.address2 - Initial address to prefill
 * @param {Function} props.setNewCity - Callback to set city from selected address
 * @param {Function} props.setNewState - Callback to set state from selected address
 * @param {Function} props.setPin - Callback to set postal code from selected address
 * @param {Function} props.setFormattedAddress - Callback to set full formatted address
 * @param {string} props.awb - Tracking or ID for analytics
 * @param {Function} props.setLocation - Callback to set geo-coordinates ({lat, lng})
 * @param {Function} props.setIsAddressFromGoogle - Callback to set address source
 * @param {boolean} props.isRedInputBorder - Flag to indicate red border for input
 * @param {Function} props.setIsRedInputBorder - Callback to set red border state
 */
const SearchLocationInput = ({
  labelText = "Address",
  placeholderText = "Address",
  address2,
  setNewCity,
  setNewState,
  setPin,
  setFormattedAddress,
  awb,
  setLocation,
  setIsAddressFromGoogle,
  isRedInputBorder = false,
  setIsRedInputBorder,
  // Optional overrides; without them the default look is unchanged.
  classes,
}) => {
  const [query, setQuery] = useState(address2);
  const [addressObject1, setAddressObject1] = useState(null);

  const { isLoaded } = useGoogleMapsScript();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Util to extract a single component from address components
  const getComponent = (type, components) =>
    components.find((c) => c.types.includes(type))?.long_name;

  // Initialize Google Places Autocomplete after script is loaded
  useEffect(() => {
    /**
     * Called when a place is selected from autocomplete dropdown.
     * Extracts address components and updates external state via callbacks.
     */
    const handlePlaceSelect = async (updateQuery) => {
      try {
        const place = autocompleteRef.current?.getPlace();
        setIsAddressFromGoogle(true);

        if (
          !place ||
          !place.geometry ||
          !place.address_components ||
          !place.formatted_address
        ) {
          console.warn(
            "Invalid or incomplete place object from Google Autocomplete",
            place
          );
          return;
        }

        const {
          name: addressName,
          formatted_address,
          geometry,
          address_components,
        } = place;

        // Funnel: an address picked from the Google list (every pick).
        track("tp_manage_changed", { field: "address", choice: "google_pick" });

        setIsRedInputBorder && setIsRedInputBorder(false);
        setAddressObject1(place);

        const isNameIncluded = formatted_address.includes(addressName);
        const fullAddress = `${
          !isNameIncluded ? `${addressName}, ` : ""
        }${formatted_address}`;
        updateQuery(fullAddress);
        setFormattedAddress(fullAddress);

        const lat = geometry.location?.lat?.();
        const lng = geometry.location?.lng?.();
        if (lat && lng) setLocation({ lat, lng });

        setPin(getComponent("postal_code", address_components));
        setNewCity(
          getComponent("administrative_area_level_3", address_components)
        );
        setNewState(
          getComponent("administrative_area_level_1", address_components)
        );
      } catch (error) {
        console.error("Error in place select:", error);
      }
    };

    if (isLoaded && inputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: "IN" },
          fields: [
            "formatted_address",
            "name",
            "address_components",
            "geometry",
          ],
        }
      );

      autocompleteRef.current.addListener("place_changed", () => {
        handlePlaceSelect(setQuery);
      });
    }
  }, [
    awb,
    isLoaded,
    setFormattedAddress,
    setIsAddressFromGoogle,
    setIsRedInputBorder,
    setLocation,
    setNewCity,
    setNewState,
    setPin,
  ]);

  // Update query whenever external `address2` prop changes
  useEffect(() => {
    setQuery(address2);
  }, [address2]);

  /**
   * Handles manual text input changes. If user types a custom address,
   * previously selected address data is reused (if available).
   */
  const onChangeValue = (value) => {
    setQuery(value);
    // address2 is the last accepted address (the saved one or the last Google
    // pick), so typing it back, e.g. after an undo, makes the field valid again.
    setIsAddressFromGoogle(value.trim() === (address2 ?? "").trim());

    if (addressObject1) {
      const components = addressObject1.address_components || [];

      setPin(getComponent("postal_code", components));
      setNewCity(getComponent("administrative_area_level_3", components));
      setNewState(getComponent("administrative_area_level_1", components));
      setFormattedAddress(addressObject1.formatted_address);
    }
  };

  if (classes) {
    return (
      <label className={classes.root}>
        <span className={classes.label}>{labelText}</span>
        <input
          ref={inputRef}
          className={`${classes.input} ${isRedInputBorder ? classes.invalid : ""}`}
          onChange={(e) => onChangeValue(e.target.value)}
          placeholder={placeholderText}
          value={query ?? ""}
          disabled={!isLoaded}
        />
      </label>
    );
  }

  return (
    <div className={styles.search_location_input}>
      <label>{labelText}</label>
      <input
        ref={inputRef}
        className={`form-control ${
          isRedInputBorder ? styles.initialRender : ""
        }`}
        onChange={(e) => onChangeValue(e.target.value)}
        style={{ borderColor: isRedInputBorder ? "red" : "rgb(195, 204, 195)" }}
        placeholder={placeholderText}
        value={query}
        disabled={!isLoaded}
      />
    </div>
  );
};

export default SearchLocationInput;
